package com.dotdrives.web.pages.settings

import com.dotdrives.controller.SettingsController
import com.dotdrives.controller.SettingsUiState
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.Tag
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotSkeleton
import kotlinx.browser.window
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface TagsPanelProps : Props {
    var controller: SettingsController
    var state: SettingsUiState
    var canWrite: Boolean
}

/** A palette of preset colors -- `tags.color` stores one hex string, no separate bg/fg pair; the pill's tint is derived from it (see [tagPillStyle]). */
private val TAG_COLOR_CHOICES = listOf(
    "#5B4AA8", "#F26223", "#4696EC", "#00A65A", "#9A6700", "#C22C25", "#88949C",
)

val TagsPanel = FC<TagsPanelProps> { props ->
    val state = props.state
    val openerId = react.useRef<String>(null)
    val (archiving, setArchiving) = react.useState<Tag?>(null)
    react.useEffect(state.tags, state.addTagSaving) {
        if (archiving != null && !state.addTagSaving && state.tags.none { it.id == archiving.id }) setArchiving(null)
    }
    useSettingsOutcomeFocus(archiving != null || state.addTagOpen, state.tagFeedback, "settings-tag-feedback", if (state.addTagOpen) null else openerId.current)
    SettingsManagementStyles {}
    state.tagFeedback?.let { feedback -> div { id = "settings-tag-feedback".unsafeCast<web.dom.ElementId>(); tabIndex = -1; role = "status".unsafeCast<react.dom.aria.AriaRole>(); className = ClassName("settings-management-feedback"); +feedback } }

    state.tagsError?.let { error ->
        DotBanner {
            message = error
            onRetry = props.controller::retry
            className = "settings-inline-banner"
        }
    }

    when {
        state.tagsLoading && state.tags.isEmpty() -> div {
            className = ClassName("settings-section-card")
            DotSkeleton { height = "30px" }
        }
        state.tags.isEmpty() && state.tagsError == null && state.tagsLoaded -> DotEmptyState {
            emoji = "🏷"
            title = "No tags yet"
            body = "Tags help you filter givers and drives -- create one to start building the catalog."
            primaryAction = if (props.canWrite) DotAction("+ New tag", props.controller::openAddTag) else null

        }
        state.tags.isEmpty() && state.tagsError != null -> Unit
        else -> {
            div {
                className = ClassName("settings-section-card")
                div { className = ClassName("settings-section-title"); +"Tags" }
                div {
                    className = ClassName("settings-section-subtitle")
                    // No delete/update policy on `tags` (docs/schema.md) -- a tag can be
                    // created but never renamed, recolored, or removed from this client, so no
                    // "x" remove affordance is rendered on the pills below.
                    +"${state.tags.size} ${if (state.tags.size == 1) "tag" else "tags"}. Tags organize givers and drives. Renaming updates existing assignments; archiving keeps assignments and removes the tag from this catalog."
                }
                div {
                    className = ClassName("settings-tag-row")
                    state.tags.forEach { tag ->
                        div {
                            key = tag.id.unsafeCast<Key>()
                            className = ClassName("settings-management-row")
                            span { className = ClassName("settings-tag-pill"); style = tagPillStyle(tag.color); +tag.name }
                            if (props.canWrite) {
                                div {
                                    className = ClassName("settings-management-row-actions")
                                    button {
                                        type = ButtonType.buttonType; className = ClassName("dot-button dot-button-secondary")
                                        ariaLabel = "Edit ${tag.name}"; onClick = { props.controller.editTag(tag) }; +"Edit"
                                    }
                                    button {
                                        type = ButtonType.buttonType; className = ClassName("dot-button dot-button-secondary")
                                        id = "settings-tag-archive-${tag.id}".unsafeCast<web.dom.ElementId>()
                                        ariaLabel = "Archive ${tag.name}"; disabled = state.addTagSaving
                                        onClick = { openerId.current = "settings-tag-archive-${tag.id}"; setArchiving(tag) }; +"Archive"
                                    }
                                }
                            }
                        }
                    }
                    if (props.canWrite) {
                        DotButton {
                            text = "+ New tag"
                            onClick = props.controller::openAddTag
                        }
                    }
                }
            }
        }
    }

    if (state.addTagOpen) {
        AddTagInline { controller = props.controller; this.state = state }
    }
    archiving?.let { tag ->
        SettingsConfirmDialog {
            title = "Archive ${tag.name}?"
            message = "Existing giver and drive assignments will remain. This tag will be removed from the active catalog."
            action = "Archive tag"; busy = state.addTagSaving; error = state.tagsError
            onClose = { setArchiving(null) }; onConfirm = { props.controller.archiveTag(tag) }
        }
    }

}

private fun tagPillStyle(color: String): dynamic {
    val pillStyle = js("({})")
    pillStyle.background = "${color}22"
    pillStyle.color = "#222D32"
    return pillStyle
}

private fun swatchStyle(color: String): dynamic {
    val swatchStyle = js("({})")
    swatchStyle.background = color
    return swatchStyle
}

private external interface AddTagInlineProps : Props {
    var controller: SettingsController
    var state: SettingsUiState
}

private val AddTagInline = FC<AddTagInlineProps> { props ->
    val (name, setName) = react.useState(props.state.editingTag?.name ?: "")
    val (color, setColor) = react.useState(props.state.editingTag?.color ?: TAG_COLOR_CHOICES.first())
    val state = props.state
    com.dotdrives.web.ui.useDialogFocus(true, "settings-tag-dialog", onClose = props.controller::closeAddTag)

    div {
        className = ClassName("settings-dialog-backdrop")
        role = "presentation".unsafeCast<react.dom.aria.AriaRole>()
        onClick = { props.controller.closeAddTag() }

        div {
            id = "settings-tag-dialog".unsafeCast<web.dom.ElementId>()
            className = ClassName("settings-dialog settings-management-dialog")
            role = "dialog".unsafeCast<react.dom.aria.AriaRole>()
            ariaModal = true
            ariaLabel = if (state.editingTag == null) "New tag" else "Edit tag"
            onClick = { it.stopPropagation() }

            div { className = ClassName("settings-dialog-title"); +(if (state.editingTag == null) "New tag" else "Edit tag") }

            state.addTagError?.let { error ->
                DotBanner { message = error; className = "settings-inline-banner" }
            }

            div {
                className = ClassName("settings-form-field")
                div { className = ClassName("settings-form-label"); +"Name" }
                input {
                    type = InputType.text
                    value = name
                    placeholder = "Major donor"
                    ariaLabel = "Tag name"
                    onChange = { event -> setName(event.currentTarget.value) }
                }
            }

            div {
                className = ClassName("settings-form-field")
                div { className = ClassName("settings-form-label"); +"Color" }
                div {
                    className = ClassName("settings-tag-color-row")
                    TAG_COLOR_CHOICES.forEach { choice ->
                        button {
                            key = choice.unsafeCast<Key>()
                            type = ButtonType.buttonType
                            className = ClassName(
                                "settings-tag-swatch" + if (choice == color) " settings-tag-swatch-selected" else "",
                            )
                            style = swatchStyle(choice)
                            ariaLabel = tagColorName(choice)
                            title = tagColorName(choice)
                            ariaPressed = (choice == color).unsafeCast<react.dom.aria.AriaPressed>()
                            onClick = { setColor(choice) }
                            if (choice == color) +"✓"
                        }
                    }
                }
            }

            div {
                className = ClassName("settings-management-preview")
                +"Preview · ${tagColorName(color)} "
                span { className = ClassName("settings-tag-pill"); style = tagPillStyle(color); +(name.trim().ifBlank { "Tag name" }) }
            }
            div {
                className = ClassName("settings-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.controller::closeAddTag
                }
                DotButton {
                    text = "Save tag"
                    loading = state.addTagSaving
                    enabled = name.isNotBlank()
                    onClick = { props.controller.submitTag(name, color) }
                }
            }
        }
    }
}

private fun tagColorName(color: String): String = mapOf("#5B4AA8" to "Purple", "#F26223" to "Orange", "#4696EC" to "Blue", "#00A65A" to "Green", "#9A6700" to "Gold", "#C22C25" to "Red", "#88949C" to "Gray")[color] ?: "Custom color"
